import { useTranslation } from 'react-i18next';
import {
    VStack,
    HStack,
    Text,
    Button,
    Box,
    Badge,
    IconButton,
} from '@chakra-ui/react';
import { Trash2, Download } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { useWorksharingStore } from '../../state/useWorksharingStore';
import { ManageDialogFrame, ManageDialogCloseFooter } from '../manage/ManageDialogFrame';
import { notify } from '../../utils/notify';

function formatTime(ts: number | null): string {
    if (!ts) return '—';
    return new Date(ts).toLocaleString();
}

/**
 * Cloud Models dialog (Collaborate > Communicate > Cloud Models).
 * Lists the central models hosted on the cloud hub with their version
 * history, lets the user open a version (reload) or delete a model.
 */
export function CloudModelsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.collabDialog === 'cloudModels');
    const onClose = useAppStore((state) => state.closeCollabDialog);
    const cloudModels = useWorksharingStore((s) => s.cloudModels);
    const deleteCloudModel = useWorksharingStore((s) => s.deleteCloudModel);
    const reloadLatest = useWorksharingStore((s) =>s.reloadLatest); return (<ManageDialogFrame
            title={t('collab.cloudModels')}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={<ManageDialogCloseFooter onClose={onClose} />}
        >
            <VStack spacing={3} align="stretch">
                <Text fontSize="xs" color="var(--color-text-secondary)">{t('collab.centralModelsSynchronizedToTheCloudHubOpenAModelVersionToReloadThe')}</Text>

                {cloudModels.length === 0 ? (
                    <Box p={4} textAlign="center" borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-sm)">
                        <Text fontSize="sm" color="var(--color-text-secondary)">{t('collab.noCloudModelsYetEnableCollaborationAndSynchronizeToCreateOne')}</Text>
                    </Box>
                ) : (
                    cloudModels.map((cm) => (
                        <Box
                            key={cm.id}
                            p={3}
                            borderWidth="1px"
                            borderColor="var(--color-border)"
                            borderRadius="var(--cad-radius-sm)"
                            bg="var(--color-bg-primary)"
                        >
                            <HStack spacing={3}>
                                <VStack align="start" spacing={0} flex="1 1 auto" minW={0}>
                                    <Text fontSize="sm" fontWeight="semibold" isTruncated>{cm.name}</Text>
                                    <Text fontSize="xs" color="var(--color-text-secondary)" isTruncated>{cm.hub}</Text>
                                    <Text fontSize="xs" color="var(--color-text-secondary)">
                                        Last synchronized: {formatTime(cm.lastSyncedAt)}
                                    </Text>
                                </VStack>
                                <Badge colorScheme="blue" flexShrink={0}>v{cm.version}</Badge>
                                <IconButton
                                    aria-label={t('collab.openLatestVersion')}
                                    icon={<Download size={14} />}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { void reloadLatest(); }}
                                />
                                <IconButton
                                    aria-label={t('collab.deleteCloudModel')}
                                    icon={<Trash2 size={14} />}
                                    size="sm"
                                    variant="ghost"
                                    color="var(--color-error)"
                                    onClick={() => {
                                        deleteCloudModel(cm.id);
                                        notify('Cloud model deleted.', 'info');
                                    }}
                                />
                            </HStack>
                        </Box>
                    ))
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
